import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {SmartDateTimeFormatterContainer} from '../../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {getValidTimestampFromValue} from '../../../../helpers/dateTimeHelpers';
import {getCellBackgroundColor, getCellIndexStyles, getCellTextColor} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

interface DateCellProps extends TableCellProps<string | number> {
  customFormat?: Intl.DateTimeFormatOptions;
}

/*
 * Styles.
 */

const StyledDateCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledTextCellValueDivProps {
  $isSelected: boolean;
}
const StyledDateCellValueDiv = styled.div<StyledTextCellValueDivProps>`
  line-height: 16px;
  ${ellipsis()};

  ${(p) => getCellTextColor(p.$isSelected)}
`;

/*
 * Component.
 */

export const DateCell = (props: DateCellProps): ReactElement => {
  const {
    customFormat,
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  return (
    <TableCell<string | number>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(value) => (
        <StyledDateCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledDateCellValueDiv $isSelected={Boolean(isSelected)}>
            <SmartDateTimeFormatterContainer
              customFormat={customFormat}
              value={typeof value === 'string' ? getValidTimestampFromValue(value) : value}
            />
          </StyledDateCellValueDiv>
        </StyledDateCellContainerDiv>
      )}
    />
  );
};
